'use client'

import React from 'react'
import { useSearchParams } from 'next/navigation'
import Wallet from '@/utils/wallet'
import { DIESEL_ID } from './lib/amm'
import { useAlkanes } from './lib/store'
import { markSessionDeviceKey, useTwetch } from './lib/twetch-store'
import useAlkanesFeed from './lib/use-alkanes-feed'
import useIsDesktop from './lib/useIsDesktop'
import AssetDetail from './components/asset-detail'
import Leaderboard from './components/leaderboard'
import SocialFeed from './components/social-feed'
import CommandPalette from '@/components/search-input/palette'
import PoolsOfflineBanner from './components/pools-status'
import StatusBar from './components/status-bar'
import TopBar from './components/top-bar'
import WalletProfile from './components/wallet-profile'
import Workspace from './components/workspace'
import TwetchProfileHost from './components/twetch-profile-host'

/// Adopt the URL without flashing the wrong market. A Home URL with no asset
/// is canonicalized in place so sharing or reloading the landing route keeps
/// the exact DIESEL market the user is looking at.
function assetFromPath(pathname: string): string | null {
	const match = /^\/alkanes\/([^/]+)\/?$/.exec(pathname)
	if (!match) return null
	try {
		const id = decodeURIComponent(match[1])
		return /^\d+:\d+$/.test(id) ? id : null
	} catch {
		return null
	}
}

/// `/alkanes/profile/:address` — the canonical public profile of any wallet.
function profileFromPath(pathname: string): string | null {
	const match = /^\/alkanes\/profile\/([^/]+)\/?$/.exec(pathname)
	if (!match) return null
	try {
		const addr = decodeURIComponent(match[1])
		return /^(bc1|1|3)[a-zA-Z0-9]{20,}$/.test(addr) ? addr : null
	} catch {
		return null
	}
}

function adoptLocation(initialAssetId?: string, initialProfileAddress?: string) {
	if (typeof window === 'undefined') {
		return
	}
	const params = new URLSearchParams(window.location.search)
	// The server-validated address is only a fallback while the URL still IS
	// the profile route (first mount); on `popstate` to another pathname it
	// must not drag the app back to the profile.
	const onProfileRoute = window.location.pathname.startsWith('/alkanes/profile/')
	let pathProfile =
		profileFromPath(window.location.pathname) ?? (onProfileRoute ? initialProfileAddress : null) ?? null
	// The retired own-profile section (`?view=profile`, and the older
	// `portfolio` / `activity` names) resolves to the ONE profile page: the
	// watched address when there is one, else the connected wallet's own.
	// With neither there is no wallet to show, and the link lands Home.
	const requestedView = params.get('view')
	const legacyProfile =
		requestedView === 'profile' || requestedView === 'portfolio' || requestedView === 'activity'
	if (legacyProfile && !pathProfile) {
		const watch = params.get('watch')
		const addr =
			(watch && /^(bc1|1|3)[a-zA-Z0-9]{20,}$/.test(watch) ? watch : null) ??
			Wallet.getTaprootAddress() ??
			null
		if (addr) {
			pathProfile = addr
			window.history.replaceState(window.history.state, '', `/alkanes/profile/${addr}`)
		} else {
			params.delete('view')
		}
	}
	const pathAsset = assetFromPath(window.location.pathname) ?? initialAssetId ?? null
	useAlkanes
		.getState()
		.syncFromLocation(pathProfile ? '' : `?${params.toString()}`, pathAsset, pathProfile)
	if (pathProfile) {
		return
	}
	if (pathAsset || useAlkanes.getState().view !== 'home') {
		return
	}
	const legacyAsset = params.get('asset') || DIESEL_ID
	const canonical = new URLSearchParams()
	const watch = params.get('watch')
	if (watch) canonical.set('watch', watch)
	window.history.replaceState(
		window.history.state,
		'',
		`/alkanes/${legacyAsset}${canonical.size ? `?${canonical.toString()}` : ''}${
			window.location.hash
		}`
	)
}

/// The /alkanes app.
///
/// A full-screen phone app that a desktop is allowed to widen — its own top
/// bar, a centered 520px column, a bottom tab bar — with the site's chrome
/// opted out at the root layout. Home is the selected market (DIESEL by
/// default), alongside Social, Leaderboard and Profile.
///
/// This file is deliberately thin. Every fact the app renders comes from the
/// store, fed by the one subscription hook below, so a section is a component
/// that reads what it draws rather than the tail of a prop chain — and a
/// twenty-second pool refresh no longer re-renders the whole page to update one
/// number inside it.
export default function AlkanesApp({
	initialAssetId,
	initialProfileAddress
}: {
	initialAssetId?: string
	initialProfileAddress?: string
}) {
	// Watch mode: ?watch=<address> renders the app read-only against that
	// address's real balances and outpoints — impersonation for QA, and a
	// genuine portfolio-viewer feature.
	const searchParams = useSearchParams()
	const watchParam = searchParams.get('watch')
	const watchAddr = watchParam && /^(bc1|1|3)[a-zA-Z0-9]{20,}$/.test(watchParam) ? watchParam : null

	// Adopt the URL BEFORE anything reads the store, not in an effect. A deep
	// link to `?asset=2:0` used to render the feed for one frame and then swap
	// to the asset screen — a visible flash of the wrong screen on the one entry
	// point that names a specific one. A state initialiser runs during the first
	// render, and it has to run ABOVE the selectors below or they read the
	// defaults it was about to replace.
	//
	// Safe to touch the store here because this subtree never renders on the
	// server: `useSearchParams` in a client component puts the whole thing
	// behind the page's Suspense fallback, so the client's first render IS the
	// first render, and there is no hydration pass to disagree with.
	React.useState(() => adoptLocation(initialAssetId, initialProfileAddress))

	useAlkanesFeed(watchAddr)

	const view = useAlkanes((s) => s.view)
	const health = useAlkanes((s) => s.health)

	// Any Twetch session this browser holds gets its key marked session-only,
	// so payouts stop landing on a key that dies with localStorage.
	const twetchIdentity = useTwetch((s) => s.identity)
	React.useEffect(() => {
		void markSessionDeviceKey(twetchIdentity)
	}, [twetchIdentity])

	// The URL is also the Back button; the store is the truth while the app is
	// open. `popstate` is the one moment the URL gets to win.
	React.useEffect(() => {
		const onPop = () => adoptLocation(initialAssetId, initialProfileAddress)
		window.addEventListener('popstate', onPop)
		return () => window.removeEventListener('popstate', onPop)
	}, [initialAssetId, initialProfileAddress])

	// ≥1024px the Home surface IS the trading workspace (rail, center, trade
	// box); below that it is the phone app. JS-gated, not CSS-gated — the two
	// are different component trees, and hiding one with CSS would still
	// mount its charts and swap panel.
	const desktop = useIsDesktop()
	const openAsset = view === 'home'

	// The server streams this tree as HTML, so its buttons are on screen
	// before React owns them — a click in that window is simply lost. Mark the
	// root once this component's effects have run: they run after every
	// child's (the palette's `command` listener included), so the attribute
	// means every door in the app is live. Set on the node directly; a state
	// update here would re-render the whole app for a flag nothing reads.
	const rootRef = React.useRef<HTMLDivElement>(null)
	React.useEffect(() => {
		rootRef.current?.setAttribute('data-hydrated', 'true')
	}, [])
	const workspace = desktop && view === 'home'

	return (
		<div ref={rootRef} className="alkanes-surface min-h-[100dvh]" data-testid="alkanes-terminal">
			<TwetchProfileHost />
			<TopBar />
			{/* Both renders of the ticker/utility strip: the desktop footer is
			    fixed and CSS-gated to lg+, the phone strip docks under the top
			    bar. Mounted once so the fee gear and health dot exist at every
			    width. */}
			<StatusBar />

			{workspace ? (
				<main className={desktop ? 'pb-8' : undefined}>
					{health === 'down' && (
						<div className="px-2 pt-2">
							<PoolsOfflineBanner />
						</div>
					)}
					<Workspace />
				</main>
			) : (
				<main
					className={
						desktop
							? 'w-full pb-16'
							: openAsset
							? 'mx-auto w-full max-w-[520px] px-3 pb-10 pt-3'
							: 'mx-auto w-full max-w-[520px] pb-10'
					}
				>
					{health === 'down' && (
						<div
							className={
								openAsset || desktop ? 'mx-auto mb-3 max-w-[520px] px-3 pt-3' : 'px-3 pt-3'
							}
						>
							<PoolsOfflineBanner />
						</div>
					)}

					{/* No entrance animation on the section itself, deliberately. A
					    `fade-in` here starts the content at opacity 0 and relies on the
					    animation completing to reveal it — and animations are throttled
					    to a crawl in a backgrounded tab, which is exactly how a section
					    ends up rendered, correct, and invisible. The motion in this app
					    is on things that convey state instead: the sheet's slide, press
					    feedback, and skeletons shaped like what replaces them. */}
					{view === 'home' && <AssetDetail />}
					{view === 'social' && (
						<div className={desktop ? 'mx-auto max-w-[520px]' : undefined}>
							<SocialFeed />
						</div>
					)}
					{view === 'leaderboard' && (
						<div className={desktop ? 'mx-auto max-w-[520px]' : undefined}>
							<Leaderboard />
						</div>
					)}
					{view === 'wallet' && <WalletProfile />}
				</main>
			)}


			{/* The site's command palette, mounted headlessly: the app has no
			    site header (and so no SearchInput), which left the `command`
			    bus without a consumer here. Every connect door in the app
			    emits `command:connect-wallet` — the SAME flow the rest of the
			    site opens. No `/` hotkey (the token search owns that key);
			    `alkanes-ink` keeps the portaled dialog on the app's tokens. */}
			<CommandPalette hotkey={false} contentClassName="alkanes-ink" />
		</div>
	)
}
